import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bug, Zap, ScrollText, Wrench, BookOpen, Play, Loader2,
  Infinity as InfinityIcon, AlertTriangle, BarChart3, Terminal, ShieldCheck,
} from "lucide-react";
import api from "../api";
import toast from "react-hot-toast";
import ProfileMenu from "../components/ProfileMenu";

export default function BugTester() {
  const [target, setTarget] = useState("");
  const [scanType, setScanType] = useState("quick");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [activeTab, setActiveTab] = useState("scan");
  const [user, setUser] = useState(null);
  const [logs, setLogs] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/api/auth/me").then(r => setUser(r.data)).catch(() => nav("/login"));
  }, []);

  const log = (msg) => setLogs(prev => [...prev, { time: new Date().toTimeString().slice(0,8), msg }]);

  const runScan = async () => {
    if (!target.trim()) { toast.error("Target required"); return; }
    setBusy(true); setResult(null); setLogs([]);
    log(`Initializing ${scanType} scan...`);
    log(`Target: ${target}`);
    log("Loading vulnerability signatures...");
    try {
      const res = await api.post("/api/bug-test/run", { target: target.trim(), scan_type: scanType });
      log("Scan complete");
      setResult(res.data);
      toast.success("Scan finished");
    } catch (err) {
      log(`ERROR: ${err.response?.data?.detail || "Scan failed"}`);
      toast.error(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : "Scan failed");
    } finally { setBusy(false); }
  };

  const logout = () => { localStorage.clear(); nav("/login"); };

  const TABS = [
    { id: "scan", label: "./scan", icon: <Zap /> },
    { id: "history", label: "./history", icon: <ScrollText /> },
    { id: "tools", label: "./tools", icon: <Wrench /> },
    { id: "docs", label: "./docs", icon: <BookOpen /> },
  ];

  return (
    <div className="au-root" style={{ display: "flex", flexDirection: "column" }}>

      {/* TOP BAR */}
      <div
        className="au-glass"
        style={{
          borderRadius: 0,
          padding: "10px 24px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <span className="au-mark" style={{ width: 30, height: 30, borderRadius: 9, display: "grid", placeItems: "center", background: "var(--au-accent)", color: "var(--au-btn-text)", boxShadow: "var(--au-glow)" }}>
            <Bug size={18} aria-hidden />
          </span>
          <span className="au-mono" style={{ color: "var(--au-muted)", letterSpacing: 1, fontSize: 12 }}>
            bugscout@khojo:~$ active_session
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <ProfileMenu />
        </div>
      </div>

      {/* TABS BAR */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 8,
          padding: "12px 24px",
          borderBottom: "1px solid var(--au-hair)",
        }}
      >
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={`au-pill au-pill-sm ${activeTab === t.id ? "au-pill-primary" : "au-pill-glass"}`}
          >
            <span aria-hidden>{t.icon}</span> {t.label}
          </button>
        ))}
      </div>

      {/* MAIN */}
      <div
        style={{
          flex: 1,
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 20,
          padding: 24,
          alignItems: "start",
        }}
      >

        {/* LEFT — INPUT PANEL */}
        <div className="au-glass au-pad">
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span className="au-badge au-badge-dark"><Bug aria-hidden /></span>
              <h1 style={{ fontSize: "2rem" }}>BugScout</h1>
            </div>
            <div className="au-mono" style={{ fontSize: 11, color: "var(--au-muted)", letterSpacing: 2, marginTop: 8 }}>
              // vulnerability scanner v2.1
            </div>
          </div>

          {/* Stats */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, marginBottom: 24 }}>
            <TermStat label="scans" value="0" />
            <TermStat label="bugs_found" value="0" tone="warn" />
            <TermStat label="quota" value={<InfinityIcon size={20} aria-label="unlimited" />} muted />
          </div>

          {/* Input */}
          <div className="au-glass au-pad" style={{ marginBottom: 16 }}>
            <div className="au-mono" style={{ fontSize: 11, color: "var(--au-muted)", marginBottom: 8 }}>
              <span style={{ color: "var(--au-accent-ink)" }}>$</span> bugscout --target
            </div>
            <input
              value={target}
              onChange={e => setTarget(e.target.value)}
              onKeyDown={e => e.key === "Enter" && !busy && runScan()}
              placeholder="https://target.example.com"
              autoFocus
              className="au-input au-mono"
            />

            <div className="au-mono" style={{ marginTop: 14, fontSize: 11, color: "var(--au-muted)", marginBottom: 8 }}>
              <span style={{ color: "var(--au-accent-ink)" }}>$</span> --scan-mode
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8 }}>
              {["quick", "standard", "deep"].map(t => (
                <button
                  key={t}
                  onClick={() => setScanType(t)}
                  className={`au-pill au-pill-sm ${scanType === t ? "au-pill-primary" : "au-pill-glass"}`}
                  style={{ letterSpacing: 1 }}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={runScan}
            disabled={busy}
            className={`au-pill ${busy ? "au-pill-glass" : "au-pill-primary"}`}
            style={{ width: "100%", letterSpacing: 1, cursor: busy ? "wait" : "pointer" }}
          >
            {busy
              ? <><Loader2 size={18} aria-hidden /> EXECUTING...</>
              : <><Play size={18} aria-hidden /> EXECUTE SCAN</>}
          </button>

          {/* Quick presets */}
          <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid var(--au-hair)" }}>
            <div className="au-marker" style={{ marginBottom: 12 }}># PRESETS</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <PresetBtn cmd="--xss --sql-i" label="Web vulnerabilities" />
              <PresetBtn cmd="--port-scan" label="Port discovery" />
              <PresetBtn cmd="--ssl-check" label="SSL/TLS audit" />
              <PresetBtn cmd="--dns --whois" label="DNS reconnaissance" />
            </div>
          </div>
        </div>

        {/* RIGHT — OUTPUT PANEL */}
        <div className="au-glass" style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {/* Output header */}
          <div
            className="au-mono"
            style={{
              padding: "12px 18px",
              fontSize: 11,
              color: "var(--au-muted)",
              letterSpacing: 1,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: "1px solid var(--au-hair)",
            }}
          >
            <span>// OUTPUT_STREAM</span>
            <span className="au-chip au-chip-status">
              <span className={`au-led ${busy ? "" : "au-led-off"}`} aria-hidden />
              {busy ? "RUNNING" : "IDLE"}
            </span>
          </div>

          {/* Progress bar */}
          {busy && (
            <div style={{ height: 3, background: "var(--au-hair)", overflow: "hidden" }}>
              <div style={{ height: "100%", width: "40%", background: "var(--au-accent)", boxShadow: "var(--au-glow)" }} />
            </div>
          )}

          {/* Logs */}
          <div className="au-mono" style={{ flex: 1, padding: 18, overflow: "auto", fontSize: 12, minHeight: 400 }}>
            {logs.length === 0 && !result && (
              <div style={{ color: "var(--au-muted)", fontStyle: "italic" }}>
                <div># Awaiting target input...</div>
                <div style={{ marginTop: 8 }}># Output will stream here in real-time</div>
                <div
                  className="au-glass"
                  style={{ marginTop: 20, padding: 14, color: "var(--au-text-2)" }}
                >
                  <div style={{ color: "var(--au-accent-ink)", marginBottom: 8, display: "flex", alignItems: "center", gap: 8 }}>
                    <ShieldCheck size={16} aria-hidden /> WELCOME, {user?.name?.toUpperCase() || "OPERATOR"}
                  </div>
                  <div>BugScout is a vulnerability scanner for web applications.</div>
                  <div style={{ marginTop: 6 }}>Enter a target URL on the left and execute scan.</div>
                  <div style={{ marginTop: 8, color: "var(--au-warn)", display: "flex", alignItems: "center", gap: 6 }}>
                    <AlertTriangle size={14} aria-hidden /> Only scan systems you own or have permission to test.
                  </div>
                </div>
              </div>
            )}

            {logs.map((l, i) => (
              <div key={i} style={{ marginBottom: 4, color: l.msg.startsWith("ERROR") ? "var(--au-error)" : "var(--au-accent-ink)" }}>
                <span style={{ color: "var(--au-muted)" }}>[{l.time}]</span> {l.msg}
              </div>
            ))}

            {result && (
              <div className="au-glass" style={{ marginTop: 20, padding: 16 }}>
                <div style={{ color: "var(--au-accent-ink)", marginBottom: 10, fontWeight: 700, fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}>
                  <BarChart3 size={16} aria-hidden /> SCAN RESULTS
                </div>
                <pre className="au-mono" style={{ margin: 0, fontSize: 11, color: "var(--au-text-2)", overflow: "auto", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{JSON.stringify(result, null, 2)}</pre>
              </div>
            )}
          </div>

          {/* Bottom status bar */}
          <div
            className="au-mono"
            style={{
              padding: "8px 18px",
              fontSize: 10,
              color: "var(--au-muted)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderTop: "1px solid var(--au-hair)",
            }}
          >
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <Terminal size={12} aria-hidden /> BugScout v2.1.0
            </span>
            <span>UTF-8 · LF · KHOJO_INFRA</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function TermStat({ label, value, tone, muted }) {
  const color = muted ? "var(--au-muted)" : tone === "warn" ? "var(--au-warn)" : "var(--au-accent-ink)";
  return (
    <div className="au-glass" style={{ padding: "10px 12px" }}>
      <div className="au-stat-lbl"># {label}</div>
      <div className="au-stat-num au-mono" style={{ fontSize: 18, color, marginTop: 2, display: "flex", alignItems: "center" }}>{value}</div>
    </div>
  );
}

function PresetBtn({ cmd, label }) {
  return (
    <div
      className="au-glass"
      style={{
        padding: "8px 12px",
        fontSize: 11,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 10,
      }}
    >
      <code className="au-mono" style={{ color: "var(--au-accent-ink)" }}>$ {cmd}</code>
      <span style={{ color: "var(--au-muted)", fontSize: 10 }}>{label}</span>
    </div>
  );
}
